import { FormEvent, useEffect, useMemo, useState } from 'react'
import { fetchGoals, fetchSettings, fetchWorkLogs, saveSettings } from '../utils/api'
import { getCurrentUser } from '../utils/auth'
import type { GoalItem, SettingsData, WorkLog } from '../types/okr'

type ReportsProps = { isOwner?: boolean }

type SummaryItem = { period: string; count: number; completed: number; hours: number }

function getWeekLabel(dateStr: string) {
  if (!dateStr) return '未填写周次'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  const yearStart = new Date(date.getFullYear(), 0, 1)
  const dayOfYear = Math.floor((Number(date) - Number(yearStart)) / (24 * 60 * 60 * 1000)) + 1
  const week = Math.ceil((dayOfYear + yearStart.getDay()) / 7)
  return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`
}

function buildPersonalReport(name: string, weekly: SummaryItem[], monthly: SummaryItem[], logs: WorkLog[]) {
  const latestLogs = logs
    .slice()
    .sort((a, b) => (a.date || '').localeCompare(b.date || '') * -1)
    .slice(0, 8)
  return [
    `汇报人：${name || '我'}`,
    '范围：个人工作日志提炼',
    '',
    '一、周度概览',
    ...weekly.map((w) => `- ${w.period}：${w.completed}/${w.count} 完成，${w.hours.toFixed(1)}h`),
    weekly.length === 0 ? '- 暂无周度数据' : '',
    '',
    '二、月度概览',
    ...monthly.map((m) => `- ${m.period}：${m.completed}/${m.count} 完成，${m.hours.toFixed(1)}h`),
    monthly.length === 0 ? '- 暂无月度数据' : '',
    '',
    '三、最近工作日志',
    ...latestLogs.map((log) => `- ${log.date || ''} ${log.title || ''} (${log.timeSpent || 0}h)`),
    latestLogs.length === 0 ? '- 暂无日志' : ''
  ].filter(Boolean).join('\n')
}

function Reports({ isOwner: isOwnerProp = false }: ReportsProps) {
  const currentUser = useMemo(() => getCurrentUser(), [])
  const [goals, setGoals] = useState<GoalItem[]>([])
  const [worklogs, setWorklogs] = useState<WorkLog[]>([])
  const [settings, setSettings] = useState<SettingsData | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    const load = async () => {
      setError('')
      try {
        if (!isOwnerProp) {
          const worklogList = await fetchWorkLogs()
          setWorklogs(worklogList)
          setAuthChecked(true)
          return
        }

        const [goalList, worklogList, settingsData] = await Promise.all([
          fetchGoals(),
          fetchWorkLogs(),
          fetchSettings()
        ])
        setGoals(goalList)
        setWorklogs(worklogList)
        setSettings(settingsData)
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载报告中心失败')
      } finally {
        setAuthChecked(true)
      }
    }

    load()
  }, [isOwnerProp, currentUser.userId])

  const completedWorklogs = useMemo(
    () => worklogs.filter((log) => log.status === 'completed').length,
    [worklogs]
  )

  const totalHours = useMemo(
    () => worklogs.reduce((sum, log) => sum + Number(log.timeSpent || 0), 0),
    [worklogs]
  )

  const bossVersion = useMemo(() => {
    if (!settings) return ''
    return [
      `汇报周期：${settings.weeklyReport.period}`,
      '',
      '一、本周核心成果',
      ...settings.weeklyReport.highlights.map((item, index) => `${index + 1}. ${item}`),
      '',
      '二、重点事项进展',
      ...settings.weeklyReport.progress.map((item) => `- ${item}`),
      '',
      '三、风险与需支持事项',
      ...settings.weeklyReport.risks.map((item, index) => `${index + 1}. ${item}`),
      '',
      '四、下周重点计划',
      ...settings.weeklyReport.nextWeekPlans.map((item, index) => `${index + 1}. ${item}`),
      '',
      '五、本周总结',
      settings.weeklyReport.summary
    ].join('\n')
  }, [settings])

  const weeklySummary = useMemo<SummaryItem[]>(() => {
    const map = new Map<string, SummaryItem>()
    worklogs.forEach((log) => {
      const period = getWeekLabel(log.date || '')
      const hours = Number(log.timeSpent || 0)
      const prev = map.get(period) || { period, count: 0, completed: 0, hours: 0 }
      map.set(period, {
        period,
        count: prev.count + 1,
        completed: prev.completed + (log.status === 'completed' ? 1 : 0),
        hours: prev.hours + hours
      })
    })
    return Array.from(map.values()).sort((a, b) => (a.period < b.period ? 1 : -1))
  }, [worklogs])

  const monthlySummary = useMemo<SummaryItem[]>(() => {
    const map = new Map<string, SummaryItem>()
    worklogs.forEach((log) => {
      const key = (log.date || '').slice(0, 7) || '未填写月份'
      const hours = Number(log.timeSpent || 0)
      const prev = map.get(key) || { period: key, count: 0, completed: 0, hours: 0 }
      map.set(key, {
        period: key,
        count: prev.count + 1,
        completed: prev.completed + (log.status === 'completed' ? 1 : 0),
        hours: prev.hours + hours
      })
    })
    return Array.from(map.values()).sort((a, b) => (a.period < b.period ? 1 : -1))
  }, [worklogs])

  const personalReport = useMemo(
    () => buildPersonalReport(currentUser.name, weeklySummary, monthlySummary, worklogs),
    [currentUser.name, weeklySummary, monthlySummary, worklogs]
  )

  const copyBossVersion = async () => {
    if (!bossVersion) return
    try {
      await navigator.clipboard.writeText(bossVersion)
      setMessage('老板版周报已复制')
    } catch {
      setError('复制失败，请手动复制')
    }
  }

  const copyPersonalVersion = async () => {
    try {
      await navigator.clipboard.writeText(personalReport)
      setMessage('个人日志报告已复制')
    } catch {
      setError('复制失败，请手动复制')
    }
  }

  const handleSave = async (event: FormEvent) => {
    event.preventDefault()
    if (!settings) return
    setSaving(true)
    setMessage('')
    setError('')
    try {
      const payload = { ...settings, updatedAt: new Date().toISOString() }
      await saveSettings(payload)
      setSettings(payload)
      setMessage('报告模板已保存')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }

  if (!authChecked) {
    return <div className="page-shell"><div className="empty-state">加载报告中心中...</div></div>
  }

  if (!isOwnerProp) {
    return (
      <div className="page-shell">
        <section className="page-header-block">
          <div>
            <span className="section-kicker">个人报告</span>
            <h2>我的日志提炼</h2>
            <p>仅显示与你相关的日志，自动汇总周度与月度概览。</p>
          </div>
          <div className="page-header-actions">
            {message && <span className="success-text">{message}</span>}
            {error && <span className="error-text">{error}</span>}
            <button className="secondary-btn" onClick={copyPersonalVersion}>复制我的报告</button>
          </div>
        </section>

        <section className="stats-grid compact-stats">
          <article className="stat-card-modern"><span>日志条数</span><strong>{worklogs.length}</strong></article>
          <article className="stat-card-modern"><span>已完成</span><strong>{completedWorklogs}</strong></article>
          <article className="stat-card-modern"><span>累计工时</span><strong>{totalHours.toFixed(1)}h</strong></article>
        </section>

        <section className="content-grid two-columns align-start">
          <div className="card-panel">
            <div className="panel-header"><h3>周度概览</h3></div>
            {weeklySummary.length === 0 && <div className="empty-state">暂无周度数据</div>}
            {weeklySummary.length > 0 && (
              <ul className="bullet-list">
                {weeklySummary.map((w) => (
                  <li key={w.period}>{w.period} · {w.completed}/{w.count} 完成 · {w.hours.toFixed(1)}h</li>
                ))}
              </ul>
            )}
          </div>

          <div className="card-panel">
            <div className="panel-header"><h3>月度概览</h3></div>
            {monthlySummary.length === 0 && <div className="empty-state">暂无月度数据</div>}
            {monthlySummary.length > 0 && (
              <ul className="bullet-list">
                {monthlySummary.map((m) => (
                  <li key={m.period}>{m.period} · {m.completed}/{m.count} 完成 · {m.hours.toFixed(1)}h</li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="card-panel">
          <div className="panel-header"><h3>最近 8 条日志</h3></div>
          {worklogs.length === 0 && <div className="empty-state">暂无日志</div>}
          {worklogs.length > 0 && (
            <ul className="bullet-list">
              {worklogs
                .slice()
                .sort((a, b) => (a.date || '').localeCompare(b.date || '') * -1)
                .slice(0, 8)
                .map((log) => (
                  <li key={log.id || `${log.date}-${log.title}`}>
                    {log.date || '未填日期'} · {log.title || '未命名'} · {log.status || 'in_progress'} · {log.timeSpent || 0}h
                  </li>
                ))}
            </ul>
          )}
        </section>

        <section className="card-panel">
          <div className="panel-header"><h3>一键复制</h3></div>
          <pre className="report-preview">{personalReport}</pre>
        </section>
      </div>
    )
  }

  if (error && !settings) {
    return <div className="page-shell"><div className="empty-state error-text">{error}</div></div>
  }

  if (!settings) {
    return <div className="page-shell"><div className="empty-state">加载报告中心中...</div></div>
  }

  return (
    <div className="page-shell">
      <section className="page-header-block">
        <div>
          <span className="section-kicker">经营复盘</span>
          <h2>报告中心</h2>
          <p>按照你的偏好默认采用 B 版本（老板版）口径，支持继续维护一页纸汇报内容。</p>
        </div>
        <div className="page-header-actions">
          {message && <span className="success-text">{message}</span>}
          {error && <span className="error-text">{error}</span>}
          <button className="secondary-btn" onClick={copyBossVersion}>复制老板版周报</button>
        </div>
      </section>

      <section className="stats-grid compact-stats">
        <article className="stat-card-modern"><span>已沉淀日志</span><strong>{worklogs.length}</strong></article>
        <article className="stat-card-modern"><span>已完成事项</span><strong>{completedWorklogs}</strong></article>
        <article className="stat-card-modern"><span>下周计划</span><strong>{settings.weeklyReport.nextWeekPlans.length}</strong></article>
      </section>

      <section className="content-grid two-columns align-start">
        <form className="card-panel form-panel" onSubmit={handleSave}>
          <div className="panel-header"><h3>周报配置</h3></div>
          <div className="form-grid">
            <label>
              <span>汇报周期</span>
              <input
                value={settings.weeklyReport.period}
                onChange={(e) => setSettings({ ...settings, weeklyReport: { ...settings.weeklyReport, period: e.target.value } })}
              />
            </label>
            <label>
              <span>偏好版本</span>
              <input
                value={settings.reporting.preferredVersion}
                onChange={(e) => setSettings({ ...settings, reporting: { ...settings.reporting, preferredVersion: e.target.value } })}
              />
            </label>
            <label className="full-span">
              <span>核心成果（每行一条）</span>
              <textarea
                rows={5}
                value={settings.weeklyReport.highlights.join('\n')}
                onChange={(e) => setSettings({
                  ...settings,
                  weeklyReport: { ...settings.weeklyReport, highlights: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) }
                })}
              />
            </label>
            <label className="full-span">
              <span>重点事项进展（每行一条）</span>
              <textarea
                rows={5}
                value={settings.weeklyReport.progress.join('\n')}
                onChange={(e) => setSettings({
                  ...settings,
                  weeklyReport: { ...settings.weeklyReport, progress: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) }
                })}
              />
            </label>
            <label className="full-span">
              <span>风险与支持事项（每行一条）</span>
              <textarea
                rows={4}
                value={settings.weeklyReport.risks.join('\n')}
                onChange={(e) => setSettings({
                  ...settings,
                  weeklyReport: { ...settings.weeklyReport, risks: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) }
                })}
              />
            </label>
            <label className="full-span">
              <span>下周重点计划（每行一条）</span>
              <textarea
                rows={5}
                value={settings.weeklyReport.nextWeekPlans.join('\n')}
                onChange={(e) => setSettings({
                  ...settings,
                  weeklyReport: { ...settings.weeklyReport, nextWeekPlans: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) }
                })}
              />
            </label>
            <label className="full-span">
              <span>总结</span>
              <textarea
                rows={4}
                value={settings.weeklyReport.summary}
                onChange={(e) => setSettings({ ...settings, weeklyReport: { ...settings.weeklyReport, summary: e.target.value } })}
              />
            </label>
          </div>
          <button className="primary-btn" disabled={saving}>{saving ? '保存中...' : '保存报告内容'}</button>
        </form>

        <div className="card-panel">
          <div className="panel-header"><h3>老板版预览</h3></div>
          <pre className="report-preview">{bossVersion}</pre>
        </div>
      </section>

      <section className="content-grid two-columns">
        <div className="card-panel">
          <div className="panel-header"><h3>与目标的对应关系</h3></div>
          <ul className="bullet-list">
            {goals.slice(0, 8).map((goal) => (
              <li key={goal.id}>{goal.title} · {goal.level === 'monthly' ? '月度目标' : goal.level === 'weekly' ? '周目标' : '任务项'} · {goal.status}</li>
            ))}
          </ul>
        </div>
        <div className="card-panel">
          <div className="panel-header"><h3>沉淀来源</h3></div>
          <ul className="bullet-list">
            {settings.sources.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="helper-text">系统已根据你已提供的历史内容完成首轮维护，后续可直接在系统内继续更新。</p>
        </div>
      </section>
    </div>
  )
}

export default Reports
